import cookies from 'js-cookie'
import 'intersection-observer'
import md5 from 'md5'

import {
  AD_SERVER_BROWSER_ID_SYNC,
  AD_SERVER_USER_ID_COOKIE,
} from 'shared/consts/adServer'
import { KEVEL_AD_SERVER_VARIANT } from 'shared/experiments/consts'
import logger from 'shared/services/logger'
import store, { subscribe } from 'shared/store'
import { actionNames } from 'shared/store/ducks/userSession'
import {
  getCookieOptions,
  getSetAdUserIdentity,
} from 'shared/utils/adServerUtils'
import { isCookieConsent } from 'shared/utils/cookieConsent'
import { filterValues } from 'shared/utils/objectUtils'
import {
  type AdServerContext,
  type GtmEvent,
  type GtmEventCallback,
  GtmEventType,
  type Placement,
} from 'types/adServerTypes'
import { CLIENTSIDE_HOOK_DATA_ATTR } from 'views/assets/scripts/consts'
import Storage from 'views/assets/scripts/services/storage/storage'
import {
  AD_BANNER_CLIENTSIDE_HOOK,
  AD_BANNER_CLOSE_CLIENTSIDE_HOOK,
} from 'views/consts/adBanner'
import buildClientAPI from 'views/providers/clientAPI'

type User = {
  customerNumber: string
}

interface UserSession {
  isLoggedIn: boolean
  user: User
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any

const storage = new Storage('localStorage')

export const loadAdServerDeps = () => {
  const {
    publicConfig: {
      adServer: { widgetUrl },
    },
  } = store.getPublicRuntimeConfig()
  const mainScriptTag = document.createElement('script')
  mainScriptTag.setAttribute('defer', '')
  mainScriptTag.src = widgetUrl
  mainScriptTag.onerror = err => {
    logger.error(`Failed to load ad-server client script: ${err}`)
  }
  document.head.appendChild(mainScriptTag)
}

export function syncUser(userSession: UserSession) {
  const { isLoggedIn, user: { customerNumber = '' } = {} } = userSession
  if (isLoggedIn) {
    // c -> refers to `browserIdConversion` is paired up with user id or not
    // t -> refers to `browserIdTargeting` is paired up with user id or not
    const { c, t } = JSON.parse(cookies.get(AD_SERVER_BROWSER_ID_SYNC) || '{}')

    const cookie = { c, t }

    let syncRequired = false

    if (!c) {
      cookie.c = true
      syncRequired = true
    }

    if (!t && isCookieConsent(cookies)) {
      cookie.t = true
      syncRequired = true
    }

    if (syncRequired) {
      // Use NOOP write converter to disable js-cookie's escaping of the values
      // https://github.com/js-cookie/js-cookie/tree/latest#encoding
      // This `never` is confusing, but needed, since @types/js-cookie defines
      // value as `string | T`, but we are using JSON.stringify, so we always
      // get a string here. We can’t use `string`, because `T extends object`.
      cookies
        .withConverter<never>({
          write: (value: string) => value,
        })
        .set(
          AD_SERVER_BROWSER_ID_SYNC,
          JSON.stringify(cookie),
          getCookieOptions(AD_SERVER_BROWSER_ID_SYNC, false)
        )
      // Trigger sync only if cookie was set successfully
      if (cookies.get(AD_SERVER_BROWSER_ID_SYNC)) {
        // TODO We only want MD5 of the customer number, ideally that would happen server side
        cookies.set(AD_SERVER_USER_ID_COOKIE, md5(customerNumber))

        window._slots.push([
          'syncUser',
          {
            userId: cookies.get(AD_SERVER_USER_ID_COOKIE),
          },
        ])
      }
    }
  }
}

export function syncInterest() {
  const { secondLevelCategories } = window._targetingContext || {}
  if (secondLevelCategories && isCookieConsent(cookies)) {
    const { browserIdTargeting } = getSetAdUserIdentity(cookies)
    window._slots.push([
      'syncInterest',
      {
        visitedCategories: secondLevelCategories,
        browserIdTargeting,
      },
    ])
  }
}

const concatIds = (placement: Placement) => {
  const { campaignId, flightId, creativeId } = placement

  return `${campaignId}:${flightId}:${creativeId}`
}

export const pushGtmEvent = (
  eventType: GtmEventType,
  placement: Placement,
  eventCallback?: GtmEventCallback
): void => {
  const ids: string = concatIds(placement)
  const event: GtmEvent = {
    event: eventType,
    [eventType]: ids,
  }

  if (eventCallback) {
    event.eventCallback = eventCallback
  }

  window.dataLayer.push(event)
}

const pushInitCommand = () => {
  const identity = filterValues(Boolean, getSetAdUserIdentity(cookies))

  window._slots = window._slots || []
  window._slots.push([
    'init',
    {
      identity,
      getKeywords: () => window._targetingKeywords || [],
      getContext: () => window._targetingContext || {},
      onView: (placement: Placement) => {
        pushGtmEvent(GtmEventType.viewEvent, placement)
      },
      onImpression: (placement: Placement) => {
        pushGtmEvent(GtmEventType.impressionEvent, placement)
      },

      // if GTM `eventCallback` fails to fire the gtm event
      // It leads that the click url might not work
      // So as a fallback, there is a setTimeout to trigger click event
      onClick: (placement: Placement) => {
        return new Promise(resolve => {
          pushGtmEvent(GtmEventType.clickEvent, placement, resolve)
        })
      },
      handleError(error: Error) {
        logger.error(error)
      },
      adEngine: KEVEL_AD_SERVER_VARIANT,
    },
  ])
}

export const loadBanners = ({
  context,
  slots,
}: {
  context?: AdServerContext
  slots: NodeListOf<HTMLElement> | Array<HTMLElement>
}): void => {
  window._slots = window._slots || []
  window._slots.push(['load', { slots, context }])
}

const loadAutoLoadSlots = () => {
  // This one gathers all slots that will be rendered automatically when
  // initialising adServer. In order to make sure that your ad is placed,
  // pls include data-auto-load attr into your placeholder/slot.
  const slots: NodeListOf<HTMLElement> = document.querySelectorAll(
    `[data-clientside-hook="${AD_BANNER_CLIENTSIDE_HOOK}"][data-auto-load]`
  )

  loadBanners({ slots })
}

export const initAdServer = () => {
  loadAdServerDeps()
  pushInitCommand()
  loadAutoLoadSlots()
  syncInterest()

  // @ts-ignore -- .once is not properly typed by us
  subscribe.after.once(
    actionNames.setUserSession,
    ({ userSession }: { userSession: UserSession }) => {
      syncUser(userSession)
    }
  )
}

export const attachViewEvent = (url: string, slot: HTMLElement): void => {
  const { clientFockService } = buildClientAPI()
  const observer = new IntersectionObserver(
    (entries: Array<IntersectionObserverEntry>) => {
      entries.forEach(async entry => {
        if (entry.isIntersecting || entry.boundingClientRect.top <= 0) {
          await clientFockService.trackEvents(url)
          observer.unobserve(entry.target)
        }
      })
    },
    {
      rootMargin: '0px',
      threshold: 1,
    }
  )

  observer.observe(slot)
}

export const handleCloseAdBanner = () => {
  const closeBannerButtons = [
    ...document.querySelectorAll(
      `[${CLIENTSIDE_HOOK_DATA_ATTR}="${AD_BANNER_CLOSE_CLIENTSIDE_HOOK}"]`
    ),
  ]

  if (closeBannerButtons.length) {
    closeBannerButtons.forEach(closeBannerButton => {
      closeBannerButton.addEventListener('click', () => {
        const id = closeBannerButton?.parentElement?.getAttribute('id')

        if (id) {
          closeBannerButton.parentElement?.remove()
          storage.set(id, true)
        }
      })
    })
  }
}
